import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '~/redux/store';
import { Roles } from '~/enums/Roles';
import { getBuildingNotes, saveBuildingNote, toggleNotePinned } from '~/api/notes';

import Breadcrumbs from '~/components/Breadcrumbs/Breadcrumbs';
import Spacer from '~/components/Spacer/Spacer';
import Spinner from '~/components/Spinner/Spinner';
import { ReactComponent as PinIcon } from '~/utils/images/pin.svg';
import { ReactComponent as NotFound } from '~/utils/images/not-found.svg';

import styles from './BuildingNotes.module.css';

interface BuildingNote {
  uuid: string;
  building: string;
  who: string;
  who_name: string;
  note: string;
  pinned: boolean;
  createdAt: string;
  updatedAt: string;
}

const BuildingNotes: React.FC = () => {
  const { buildingId } = useParams<{ buildingId: string }>();
  const userId = useSelector((state: RootState) => state.auth.user.uuid);
  const { userRole } = useSelector((state: RootState) => state.auth);
  
  const [pinnedNotes, setPinnedNotes] = useState<BuildingNote[]>([]);
  const [otherNotes, setOtherNotes] = useState<BuildingNote[]>([]);
  const [newNote, setNewNote] = useState('');
  const [isPinned, setIsPinned] = useState(false);
  const [loading, setLoading] = useState(true);
  const isMounted = useRef(true);

  useEffect(() => {
    // Set isMounted to true when component mounts
    isMounted.current = true;
    
    // Fetch notes
    fetchBuildingNotes();
    
    // Cleanup function to run when component unmounts
    return () => {
      isMounted.current = false;
    };
  }, [buildingId]);

  const fetchBuildingNotes = async () => {
    if (!isMounted.current) return;
    
    setLoading(true);
    try {
      // Using actual API function instead of alert placeholder
      const response = await getBuildingNotes(buildingId);
      
      // Only update state if component is still mounted
      if (isMounted.current) {
        // Split notes into pinned and other
        const notes = response.data.rows || [];
        setPinnedNotes(notes.filter((note: BuildingNote) => note.pinned));
        setOtherNotes(notes.map((note: BuildingNote) => ({
          ...note,
          pinnedIcon: note.pinned ? 'pin.svg' : null
        })));
        setLoading(false);
      }
    } catch (error) {
      console.error('Error fetching building notes:', error);
      if (isMounted.current) {
        setLoading(false);
        // Keep the alert to notify of errors
        alert('Failed to fetch building notes');
      }
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim() || !isMounted.current) return;
    
    try {
      // Pass the pinned status along with the note
      await saveBuildingNote(buildingId, newNote, userId, isPinned);
      
      // Only update state if component is still mounted
      if (isMounted.current) {
        // Clear the textarea after successful save
        setNewNote('');
        setIsPinned(false);
        // Refresh notes list
        fetchBuildingNotes();
      }
    } catch (error) {
      console.error('Error saving building note:', error);
      if (isMounted.current) {
        alert('Failed to save note');
      }
    }
  };

  const handleTogglePin = async (noteId: string, currentPinned: boolean) => {
    try {
      // Using actual API function instead of alert placeholder
      await toggleNotePinned(noteId, !currentPinned);
      
      // Refresh notes list
      fetchBuildingNotes();
    } catch (error) {
      console.error('Error toggling pin status:', error);
      alert('Failed to update pin status');
    }
  };

  const formatDate = (dateString: string) => {
    const options: Intl.DateTimeFormatOptions = { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const renderNoteItem = (note: BuildingNote, isPinned: boolean, location: string) => (
    <div key={note.uuid} className={styles.noteItem}>
      <div className={styles.noteHeader}>
        <div className={styles.noteHeaderLeft}>
          <span className="small-text bold">{note.who_name}</span>
          {(userRole === Roles.Superadmin || userRole === Roles.Vcm) && (
            <button 
              className={styles.pinButton}
              onClick={() => handleTogglePin(note.uuid, note.pinned)}
              title={isPinned ? "Unpin this note" : "Pin this note"}
            >
                {location === 'pinned' && (
              <PinIcon 
                className={`${styles.pin} ${note.pinned ? styles.pinned : ''}`}
                style={{ 
                  color: note.pinned ? '#3498db' : '#999',
                  transform: note.pinned ? 'scale(1.1)' : 'scale(1)'
                }}
              />
            )}
            {location === 'other' && note.pinned && (
             <PinIcon 
             className={`${styles.pin} ${note.pinned ? styles.pinned : ''}`}
             style={{ 
               color: note.pinned ? '#3498db' : '#999',
               transform: note.pinned ? 'scale(1.1)' : 'scale(1)'
             }}
           />
            )}
            </button>
          )}
        </div>
        <span className="small-text">{formatDate(note.createdAt)}</span>
      </div>
      <Spacer height={5} />
      <div className="small-text">{note.note}</div>
    </div>
  );

  if (loading) {
    return (
      <div className="content-container float-spinner-parent">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="content-container float-spinner-parent">
      <Breadcrumbs indexesToHide={[2]} />
      <Spacer height={20} />
      <h1>Building Notes</h1>
      <Spacer height={20} />
      
      {/* 1. Pinned Notes Section */}
      <div className={styles.notesSection}>
        <h2 className={styles.sectionTitle}>Pinned Notes</h2>
        <Spacer height={10} />
        
        {pinnedNotes.length === 0 ? (
          <div className={styles.noData}>
            <NotFound />
            <h3 className={styles.titleLabel}>No pinned notes yet</h3>
          </div>
        ) : (
          <div className={styles.notesList}>
            {pinnedNotes.map(note => renderNoteItem(note, true, 'pinned'))}
          </div>
        )}
      </div>
      
      <Spacer height={30} />
      
      {/* 2. Add Note Section */}
      <div className={styles.notesSection}>
        <h2 className={styles.sectionTitle}>Add a Note</h2>
        <Spacer height={10} />
        
        <div className={styles.addNote}>
          <textarea
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
            placeholder="Write a note about this building..."
            rows={4}
            className={styles.noteTextarea}
          />
          <Spacer height={10} />

          {/* Pin checkbox - only visible to admins and VCMs */}
          {(userRole === Roles.Superadmin || userRole === Roles.Vcm) && (
            <div className={styles.pinCheckboxContainer}>
              <label className={styles.pinCheckboxLabel}>
                <input
                  type="checkbox"
                  checked={isPinned}
                  onChange={() => setIsPinned(!isPinned)}
                  className={styles.pinCheckbox}
                />
                <span className="small-text">Pin this note</span>
              </label>
              <Spacer height={10} />
            </div>
          )}

          <button 
            className="btn-standard btn-primary cursor-pointer"
            onClick={handleAddNote}
            disabled={!newNote.trim()}
          >
            Add Note
          </button>
        </div>
      </div>
      
      <Spacer height={30} />
      
      {/* 3. All Other Notes Section */}
      <div className={styles.notesSection}>
        <h2 className={styles.sectionTitle}>All Notes</h2>
        <Spacer height={10} />
        
        {otherNotes.length === 0 ? (
          <div className={styles.noData}>
            <NotFound />
            <h3 className={styles.titleLabel}>No notes yet</h3>
          </div>
        ) : (
          <div className={styles.notesList}>
            {otherNotes.map(note => renderNoteItem(note, false, 'other'))}
          </div>
        )}
      </div>
    </div>
  );
};

export default BuildingNotes; 